import { useMutation } from '@tanstack/react-query'

import type { UpdateThreadReadStatusDTO } from '@fv/models'

import { useCurrentUser } from '@/hooks/settings/useAppContext'
import { shipperFetch } from '@/utils/shipperFetch'

import {
  useUnreadCountCacheInvalidator,
  useUnreadCountCacheModifier,
  useUpdateMessagesInInboxCache,
  useUpdateMessagesInLoadCache,
} from './cache-modifiers'
import type { Message } from './types'

const putThreadReadStatus =
  (messageId: string) => (dto: UpdateThreadReadStatusDTO) =>
    shipperFetch(`/threads/${messageId}/read-status`, {
      method: 'PUT',
      body: dto,
    })

export const useUpdateThreadReadStatus = (message?: Message) => {
  const updateMessagesInInboxCache = useUpdateMessagesInInboxCache()
  const updateMessagesInLoadCache = useUpdateMessagesInLoadCache()
  const unreadModifier = useUnreadCountCacheModifier(message)
  const user = useCurrentUser()

  return useMutation({
    mutationFn: putThreadReadStatus(message?._id),
    onSettled: (_response, _error, vars) => {
      if (!message) return
      const carrierId = message.participants.find(
        p => p.role === 'carrier',
      )?.carrierId
      unreadModifier(vars.read)
      updateMessagesInInboxCache(messages =>
        messages.map(m =>
          m._id !== message._id
            ? m
            : {
                ...m,
                readDate: vars.read ? new Date().toISOString() : undefined,
              },
        ),
      )
      updateMessagesInLoadCache(message?.loadId, messages =>
        messages.map(m =>
          !m.participants.some(p => p.carrierId === carrierId)
            ? m
            : {
                ...m,
                participants: m.participants.map(p =>
                  p.id === user._id
                    ? {
                        ...p,
                        ...(vars.read
                          ? { readDate: new Date().toISOString() }
                          : { readDate: undefined }),
                      }
                    : p,
                ),
              },
        ),
      )
    },
  })
}

const archiveThread = (messageId: string) =>
  shipperFetch(`/threads/${messageId}`, {
    method: 'DELETE',
  })
export const useArchiveThread = (messageId: string) => {
  const updateMessagesInQueryCache = useUpdateMessagesInInboxCache()
  return useMutation({
    mutationFn: () => archiveThread(messageId),
    onSettled: () => {
      updateMessagesInQueryCache(messages =>
        messages.filter(m => m._id !== messageId),
      )
    },
  })
}

export const useArchiveMultipleThreads = () => {
  const unreadCountInvalidator = useUnreadCountCacheInvalidator()
  const updateMessagesInQueryCache = useUpdateMessagesInInboxCache()
  return useMutation({
    mutationFn: async (messageIds: string[]) =>
      await Promise.all(messageIds.map(archiveThread)),
    onSettled: (_, __, messageIds) => {
      updateMessagesInQueryCache(messages =>
        messages.filter(m => !messageIds.includes(m._id)),
      )
      unreadCountInvalidator()
    },
  })
}

const postMessage =
  (loadId: string) =>
  async (dto: {
    carrierId?: string
    messageText: string
  }): Promise<Message> => {
    const { carrierId, messageText } = dto
    return await shipperFetch<Message>(`/shipments/${loadId}/messages`, {
      body: {
        carrierId,
        messageText,
      },
      method: 'POST',
    })
  }

export const newMessageId = 'newMessage'
export function usePostMessage(loadId: string) {
  const updateLoadMessages = useUpdateMessagesInLoadCache()

  return useMutation({
    mutationFn: postMessage(loadId),
    onSettled: (response, error) => {
      !error &&
        updateLoadMessages(loadId, messages => [
          response,
          ...messages.filter(m => m._id !== response._id),
        ])
    },
  })
}

const subscribeToThread = (loadId: string, carrierId: string) =>
  shipperFetch(`/shipments/${loadId}/threads/${carrierId}/subscribe`, {
    method: 'POST',
  })

export const useSubscribeToThread = (loadId: string, carrierId: string) => {
  const updateMessagesInLoadCache = useUpdateMessagesInLoadCache()
  const user = useCurrentUser()

  return useMutation({
    mutationFn: () => subscribeToThread(loadId, carrierId),
    onSuccess: () => {
      // add them as a participant to all messages for brevity even though the server only does it for the latest message
      updateMessagesInLoadCache(loadId, messages =>
        messages.map(m => ({
          ...m,
          participants: [
            ...m.participants,
            {
              id: user._id,
              role: 'shipper' as const,
              isAuthor: false,
            },
          ],
        })),
      )
    },
  })
}
